import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, FileSignature, Save, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TXT_FIELDS = [
  { k: "responsabile_commerciale", label: "Responsabile Commerciale (intestazione)" },
  { k: "raee_eur_modulo", label: "Eco-contributo RAEE (€ per modulo FV)" },
  { k: "validita", label: "Validità della proposta (default)" },
  { k: "spedizione", label: "Spedizione (default)" },
  { k: "consegna", label: "Consegna a mezzo (default)" },
  { k: "azienda_ragione", label: "Ragione sociale fornitore" },
  { k: "azienda_piva", label: "P.IVA / C.F. fornitore" },
  { k: "azienda_firma", label: "Firma fornitore" },
];
const AREA_FIELDS = [
  { k: "approntamento", label: "Approntamento merce (default)" },
  { k: "footer_riga1", label: "Footer riga 1" },
  { k: "footer_riga2", label: "Footer riga 2" },
  { k: "clausola_1341", label: "Clausola art. 1341/1342 (approvazione specifica)" },
  { k: "dichiarazione_iva", label: "Dichiarazione IVA agevolata" },
  { k: "condizioni_titolo", label: "Titolo Condizioni Generali" },
];

export default function CondizioniContratto() {
  const { user } = useAuth();
  const allowed = user?.role === "admin";
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mt, setMt] = useState(null);
  const [savingMt, setSavingMt] = useState(false);
  const [pmSearch, setPmSearch] = useState({ cat: null, q: "", res: [] });

  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    Promise.all([
      api.get("/quote-terms").then((r) => setT(r.data)),
      api.get("/quote-margin-targets").then((r) => setMt(r.data)).catch(() => {}),
    ]).catch(() => toast.error("Errore caricamento")).finally(() => setLoading(false));
  }, [allowed]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/quote-terms", t);
      setT(r.data);
      toast.success("Condizioni salvate");
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const saveMargin = async () => {
    setSavingMt(true);
    try {
      const r = await api.put("/quote-margin-targets", { targets: mt.targets, targets_programmato: mt.targets_programmato, default_pct: mt.default_pct, default_pct_programmato: mt.default_pct_programmato, targets_distributori: mt.targets_distributori, default_pct_distributori: mt.default_pct_distributori, targets_spot_min: mt.targets_spot_min, default_pct_spot_min: mt.default_pct_spot_min, per_model: mt.per_model });      setMt(r.data);
      toast.success("Obiettivi di margine salvati");
    } catch { toast.error("Errore salvataggio margini"); }
    finally { setSavingMt(false); }
  };
  const setTarget = (k, v) => setMt((s) => ({ ...s, targets: { ...s.targets, [k]: v === "" ? "" : +v } }));
  const setTargetSpotMin = (k, v) => setMt((s) => ({ ...s, targets_spot_min: { ...(s.targets_spot_min || {}), [k]: v === "" ? "" : +v } }));
  const setTargetDist = (k, v) => setMt((s) => ({ ...s, targets_distributori: { ...(s.targets_distributori || {}), [k]: v === "" ? "" : +v } }));
  const setTargetProg = (sc, k, v) => setMt((s) => ({ ...s, targets_programmato: { ...(s.targets_programmato || {}), [sc]: { ...((s.targets_programmato || {})[sc] || {}), [k]: v === "" ? "" : +v } } }));
  const setDefProg = (sc, v) => setMt((s) => ({ ...s, default_pct_programmato: { ...(s.default_pct_programmato || {}), [sc]: v === "" ? "" : +v } }));
  const searchPmMod = (cat, v) => {
    setPmSearch({ cat, q: v, res: [] });
    if (v.trim().length < 2) return;
    api.get(`/listino?abilitato=1&search=${encodeURIComponent(v)}`).then((r) => {
      const added = new Set(Object.keys(mt.per_model || {}));
      const res = (r.data.products || []).filter((p) => (p.tipologia || "") === cat && !added.has(p.codice)).slice(0, 15);
      setPmSearch({ cat, q: v, res });
    }).catch(() => {});
  };
  const addPmException = (cat, p) => {
    setMt((s) => ({ ...s, per_model: { ...(s.per_model || {}), [p.codice]: { tipologia: cat, descrizione: (p.descrizione || "").slice(0, 60) } } }));
    setPmSearch({ cat: null, q: "", res: [] });
  };
  const setPmField = (code, field, v) => setMt((s) => ({ ...s, per_model: { ...(s.per_model || {}), [code]: { ...((s.per_model || {})[code] || {}), [field]: v === "" ? "" : +v } } }));
  const delPmException = (code) => setMt((s) => { const pm = { ...(s.per_model || {}) }; delete pm[code]; return { ...s, per_model: pm }; });

  const setDich = (i, v) => setT((s) => ({ ...s, dichiarazioni: s.dichiarazioni.map((x, j) => (j === i ? v : x)) }));
  const addDich = () => setT((s) => ({ ...s, dichiarazioni: [...s.dichiarazioni, ""] }));
  const delDich = (i) => setT((s) => ({ ...s, dichiarazioni: s.dichiarazioni.filter((_, j) => j !== i) }));
  const setArt = (i, field, v) => setT((s) => ({ ...s, condizioni_generali: s.condizioni_generali.map((x, j) => (j === i ? { ...x, [field]: v } : x)) }));
  const addArt = () => setT((s) => ({ ...s, condizioni_generali: [...s.condizioni_generali, { titolo: "", testo: "" }] }));
  const delArt = (i) => setT((s) => ({ ...s, condizioni_generali: s.condizioni_generali.filter((_, j) => j !== i) }));

  const servizi = t?.servizi || [];
  const setSrv = (i, field, v) => setT((s) => ({ ...s, servizi: (s.servizi || []).map((x, j) => (j === i ? { ...x, [field]: v } : x)) }));
  const addSrv = () => setT((s) => ({ ...s, servizi: [...(s.servizi || []), { codice: "", nome: "", descrizione: "", um: "Nr", tipo: "fisso", prezzo: 0, scaglioni: [], attivo: true }] }));
  const delSrv = (i) => setT((s) => ({ ...s, servizi: (s.servizi || []).filter((_, j) => j !== i) }));
  const setScag = (i, j, field, v) => setT((s) => ({ ...s, servizi: (s.servizi || []).map((x, k) => (k === i ? { ...x, scaglioni: (x.scaglioni || []).map((y, m) => (m === j ? { ...y, [field]: v === "" ? "" : +v } : y)) } : x)) }));
  const addScag = (i) => setT((s) => ({ ...s, servizi: (s.servizi || []).map((x, k) => (k === i ? { ...x, scaglioni: [...(x.scaglioni || []), { fino_a_kwp: 0, prezzo: 0 }] } : x)) }));
  const delScag = (i, j) => setT((s) => ({ ...s, servizi: (s.servizi || []).map((x, k) => (k === i ? { ...x, scaglioni: (x.scaglioni || []).filter((_, m) => m !== j) } : x)) }));
  const [modSearch, setModSearch] = useState({ idx: null, q: "", res: [] });
  const searchMod = (i, v) => {
    setModSearch({ idx: i, q: v, res: [] });
    if (v.trim().length < 2) return;
    api.get(`/listino?abilitato=1&search=${encodeURIComponent(v)}`).then((r) => setModSearch({ idx: i, q: v, res: (r.data.products || []).filter((p) => (p.tipologia || "").toLowerCase().includes("modul")).slice(0, 15) })).catch(() => {});
  };
  const addMod = (i, code) => { setSrv(i, "moduli_obbligatori", Array.from(new Set([...((servizi[i] || {}).moduli_obbligatori || []), code]))); setModSearch({ idx: null, q: "", res: [] }); };
  const delMod = (i, code) => setSrv(i, "moduli_obbligatori", ((servizi[i] || {}).moduli_obbligatori || []).filter((c) => c !== code));

  const PM_FIELD_DEFS = [
    { f: "spot_target", cls: "", ph: (k) => (mt?.targets || {})[k] },
    { f: "spot_min", cls: "", ph: (k) => (mt?.targets_spot_min || {})[k] },
    { f: "prog_s1", cls: "border-yellow-400", ph: (k) => ((mt?.targets_programmato || {}).s1 || {})[k] },
    { f: "prog_s2", cls: "border-yellow-400", ph: (k) => ((mt?.targets_programmato || {}).s2 || {})[k] },
    { f: "prog_s3", cls: "border-yellow-400", ph: (k) => ((mt?.targets_programmato || {}).s3 || {})[k] },
    { f: "distributore", cls: "border-primary/40", ph: (k) => (mt?.targets_distributori || {})[k] },
  ];

  if (loading) return <div className="py-20 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  if (!allowed) return <div className="py-20 text-center text-muted-foreground" data-testid="condizioni-denied">Accesso negato. Sezione riservata agli amministratori.</div>;
  if (!t) return null;

  return (
    <div className="space-y-6 pb-24" data-testid="condizioni-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-black"><FileSignature className="h-7 w-7 text-primary" /> Condizioni Contrattuali</h1>
          <p className="text-sm text-muted-foreground">Testi legali del PDF "Proposta contrattuale" generato dal Preventivatore.</p>
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="condizioni-save">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Salva condizioni
        </Button>
      </div>

      <Card className="space-y-3 p-4">
        <h2 className="text-lg font-bold">Intestazione e default</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {TXT_FIELDS.map((f) => (
            <div key={f.k}>
              <Label className="text-xs">{f.label}</Label>
              <Input value={t[f.k] ?? ""} onChange={(e) => setT({ ...t, [f.k]: e.target.value })} data-testid={`terms-${f.k}`} />
            </div>
          ))}
        </div>
      </Card>

      <Card className="space-y-3 p-4" data-testid="margin-targets-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Obiettivi di margine per categoria</h2>
            <p className="text-xs text-muted-foreground"><strong>Spot</strong> (consegna unica: target e minimo) e <strong>Programmato</strong> per 3 scaglioni di importo di listino lordo: ≥30.000 €, ≥80.000 €, ≥120.000 €. Alimentano l'indice di contribuzione e lo sconto applicabile nel Preventivatore.</p>
          </div>
          <Button size="sm" onClick={saveMargin} disabled={savingMt || !mt} data-testid="margin-save">
            {savingMt ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Salva margini
          </Button>
        </div>
        {mt && (
          <div className="space-y-2">
            <div className="grid grid-cols-[1fr_5rem_5rem_5rem_5rem_5rem_5rem] items-center gap-x-2 text-[11px] font-semibold text-muted-foreground">
              <span>Categoria</span><span className="text-center">Spot target %</span><span className="text-center">Spot min %</span><span className="text-center text-yellow-500">Progr. ≥30k</span><span className="text-center text-yellow-500">Progr. ≥80k</span><span className="text-center text-yellow-500">Progr. ≥120k</span><span className="text-center text-primary">Distributori</span>
            </div>
            {Object.keys(mt.targets || {}).sort().map((k) => (
              <div key={k} className="space-y-1">
                <div className="grid grid-cols-[1fr_5rem_5rem_5rem_5rem_5rem_5rem] items-center gap-x-2">
                  <Label className="text-xs">{k}</Label>
                  <Input type="number" step="0.1" value={mt.targets[k]} onChange={(e) => setTarget(k, e.target.value)} className="h-8 text-right" data-testid={`margin-target-${k}`} />
                  <Input type="number" step="0.1" value={(mt.targets_spot_min || {})[k] ?? ""} onChange={(e) => setTargetSpotMin(k, e.target.value)} className="h-8 text-right" data-testid={`margin-spotmin-${k}`} />
                  {["s1", "s2", "s3"].map((sc) => (
                    <Input key={sc} type="number" step="0.1" value={((mt.targets_programmato || {})[sc] || {})[k] ?? ""} onChange={(e) => setTargetProg(sc, k, e.target.value)} className="h-8 text-right border-yellow-400" data-testid={`margin-target-prog-${sc}-${k}`} />
                  ))}
                  <Input type="number" step="0.1" value={(mt.targets_distributori || {})[k] ?? ""} onChange={(e) => setTargetDist(k, e.target.value)} className="h-8 text-right border-primary/40" data-testid={`margin-target-dist-${k}`} />
                </div>
                {Object.entries(mt.per_model || {}).filter(([, ex]) => (ex.tipologia || "") === k).map(([code, ex]) => (
                  <div key={code} className="grid grid-cols-[1fr_5rem_5rem_5rem_5rem_5rem_5rem] items-center gap-x-2 pl-2" data-testid={`margin-pm-row-${code}`}>
                    <div className="flex items-center gap-1 min-w-0">
                      <button type="button" onClick={() => delPmException(code)} className="shrink-0 text-red-500" data-testid={`margin-pm-del-${code}`}><Trash2 className="h-3.5 w-3.5" /></button>
                      <span className="truncate font-mono text-[11px] text-muted-foreground" title={ex.descrizione || code}>↳ {code}</span>
                    </div>
                    {PM_FIELD_DEFS.map((fd) => (
                      <Input key={fd.f} type="number" step="0.1" value={ex[fd.f] ?? ""} placeholder={fd.ph(k) != null ? String(fd.ph(k)) : ""} onChange={(e) => setPmField(code, fd.f, e.target.value)} className={`h-8 text-right ${fd.cls}`} data-testid={`margin-pm-${fd.f}-${code}`} />
                    ))}
                  </div>
                ))}
                <div className="pl-2">
                  {pmSearch.cat === k ? (
                    <div className="relative max-w-md">
                      <Input autoFocus value={pmSearch.q} onChange={(e) => searchPmMod(k, e.target.value)} placeholder="Cerca modello (codice/descrizione)…" className="h-7 text-xs" data-testid={`margin-pm-search-${k}`} />
                      {pmSearch.res.length > 0 && (
                        <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-input bg-popover shadow-lg">
                          {pmSearch.res.map((p) => (
                            <button key={p.id} type="button" onClick={() => addPmException(k, p)} className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-accent" data-testid={`margin-pm-res-${p.codice}`}><span className="font-mono font-semibold">{p.codice}</span><span className="truncate text-muted-foreground">{(p.descrizione || "").slice(0, 40)}</span></button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button type="button" onClick={() => setPmSearch({ cat: k, q: "", res: [] })} className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline" data-testid={`margin-pm-add-${k}`}><Plus className="h-3.5 w-3.5" /> Aggiungi eccezione modello</button>
                  )}
                </div>
              </div>
            ))}
            <div className="grid grid-cols-[1fr_5rem_5rem_5rem_5rem_5rem_5rem] items-center gap-x-2 border-t border-input pt-2">
              <Label className="text-xs">Default (altre categorie)</Label>
              <Input type="number" step="0.1" value={mt.default_pct} onChange={(e) => setMt((s) => ({ ...s, default_pct: e.target.value === "" ? "" : +e.target.value }))} className="h-8 text-right" data-testid="margin-default" />
              <Input type="number" step="0.1" value={mt.default_pct_spot_min ?? ""} onChange={(e) => setMt((s) => ({ ...s, default_pct_spot_min: e.target.value === "" ? "" : +e.target.value }))} className="h-8 text-right" data-testid="margin-default-spotmin" />
              {["s1", "s2", "s3"].map((sc) => (
                <Input key={sc} type="number" step="0.1" value={(mt.default_pct_programmato || {})[sc] ?? ""} onChange={(e) => setDefProg(sc, e.target.value)} className="h-8 text-right border-yellow-400" data-testid={`margin-default-prog-${sc}`} />
              ))}
              <Input type="number" step="0.1" value={mt.default_pct_distributori ?? ""} onChange={(e) => setMt((s) => ({ ...s, default_pct_distributori: e.target.value === "" ? "" : +e.target.value }))} className="h-8 text-right border-primary/40" data-testid="margin-default-dist" />
            </div>
            <p className="text-[11px] text-muted-foreground pt-1"><strong>Spot min %</strong>: genera il <em>prezzo minimo installatore</em> (costo industriale maggiorato di questo margine), mostrato nel Listino online e nel PDF. Le 3 colonne <span className="text-yellow-500 font-semibold">Programmato</span> sono evidenziate in giallo. <strong className="text-primary">Distributori</strong>: obiettivo di margine riservato al canale distributori → <em>prezzo al distributore</em> nel Listino e nel PDF "Listino distributore". Con <strong>«Aggiungi eccezione modello»</strong> puoi impostare margini specifici per singoli prodotti: i campi lasciati vuoti (placeholder grigio) ereditano il valore della categoria.</p>
          </div>
        )}
      </Card>

      <Card className="space-y-3 p-4">
        <h2 className="text-lg font-bold">Testi lunghi</h2>
        {AREA_FIELDS.map((f) => (
          <div key={f.k}>
            <Label className="text-xs">{f.label}</Label>
            <Textarea value={t[f.k] ?? ""} onChange={(e) => setT({ ...t, [f.k]: e.target.value })} className="min-h-[70px] text-sm" data-testid={`terms-${f.k}`} />
          </div>
        ))}
      </Card>

      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Dichiarazioni cliente (pag. 2)</h2>
          <Button size="sm" variant="outline" onClick={addDich} data-testid="terms-add-dich"><Plus className="mr-1 h-4 w-4" /> Aggiungi</Button>
        </div>
        {(t.dichiarazioni || []).map((d, i) => (
          <div key={i} className="flex items-start gap-2">
            <Textarea value={d} onChange={(e) => setDich(i, e.target.value)} className="min-h-[60px] flex-1 text-sm" data-testid={`terms-dich-${i}`} />
            <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => delDich(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        ))}
      </Card>

      <Card className="space-y-3 p-4" data-testid="servizi-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold">Servizi (richiamabili nel Preventivatore)</h2>
            <p className="text-xs text-muted-foreground">Servizi opzionali aggiungibili al preventivo. Per le <strong>pratiche</strong> il prezzo è per scaglioni di dimensione impianto (kWp): oltre l'ultimo scaglione si usa il prezzo più alto.</p>
          </div>
          <Button size="sm" variant="outline" onClick={addSrv} data-testid="servizi-add"><Plus className="mr-1 h-4 w-4" /> Aggiungi servizio</Button>
        </div>
        {servizi.length === 0 && <p className="text-sm text-muted-foreground">Nessun servizio configurato.</p>}
        {servizi.map((s, i) => (
          <div key={i} className="space-y-2 rounded-md border border-input p-3" data-testid={`servizio-${i}`}>
            <div className="flex flex-wrap items-end gap-2">
              <div className="w-24"><Label className="text-[11px]">Codice</Label><Input value={s.codice || ""} onChange={(e) => setSrv(i, "codice", e.target.value)} className="h-8" data-testid={`servizio-${i}-codice`} /></div>
              <div className="flex-1 min-w-[220px]"><Label className="text-[11px]">Dicitura</Label><Input value={s.nome || ""} onChange={(e) => setSrv(i, "nome", e.target.value)} className="h-8" data-testid={`servizio-${i}-nome`} /></div>
              <div className="w-20"><Label className="text-[11px]">UM</Label><Input value={s.um || ""} onChange={(e) => setSrv(i, "um", e.target.value)} className="h-8" /></div>
              <div className="w-32">
                <Label className="text-[11px]">Tipo</Label>
                <select value={s.tipo || "fisso"} onChange={(e) => setSrv(i, "tipo", e.target.value)} className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm" data-testid={`servizio-${i}-tipo`}>
                  <option value="fisso">Prezzo fisso</option>
                  <option value="pratica">Pratica (per kWp)</option>
                  <option value="manuale">Prezzo manuale (solo BO)</option>
                </select>
              </div>
              {(s.tipo || "fisso") === "fisso" && <div className="w-28"><Label className="text-[11px]">Prezzo €</Label><Input type="number" value={s.prezzo ?? 0} onChange={(e) => setSrv(i, "prezzo", e.target.value === "" ? "" : +e.target.value)} className="h-8 text-right" data-testid={`servizio-${i}-prezzo`} /></div>}
              <label className="flex items-center gap-1 pb-1.5 text-[11px]"><input type="checkbox" checked={s.attivo !== false} onChange={(e) => setSrv(i, "attivo", e.target.checked)} /> Attivo</label>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => delSrv(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
            <div><Label className="text-[11px]">Descrizione (facoltativa)</Label><Textarea value={s.descrizione || ""} onChange={(e) => setSrv(i, "descrizione", e.target.value)} className="min-h-[44px] text-sm" /></div>
            <div className="rounded-md border border-dashed border-input p-2" data-testid={`servizio-${i}-moduli`}>
              <Label className="text-[11px] font-semibold">Obbligatorio per questi moduli (es. casse per certi pannelli)</Label>
              <div className="mt-1 flex flex-wrap gap-1">
                {(s.moduli_obbligatori || []).map((c) => (
                  <span key={c} className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary" data-testid={`servizio-${i}-mod-${c}`}>{c}<button type="button" onClick={() => delMod(i, c)} className="text-red-500">×</button></span>
                ))}
                {!(s.moduli_obbligatori || []).length && <span className="text-[11px] text-muted-foreground">Nessuno</span>}
              </div>
              <div className="relative mt-1">
                <Input value={modSearch.idx === i ? modSearch.q : ""} onChange={(e) => searchMod(i, e.target.value)} placeholder="Cerca modulo (codice/descrizione)…" className="h-8" data-testid={`servizio-${i}-modsearch`} />
                {modSearch.idx === i && modSearch.res.length > 0 && (
                  <div className="absolute z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-input bg-popover shadow-lg">
                    {modSearch.res.map((p) => (
                      <button key={p.id} type="button" onClick={() => addMod(i, p.codice)} className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-accent" data-testid={`servizio-${i}-modres-${p.codice}`}><span className="font-mono font-semibold">{p.codice}</span><span className="truncate text-muted-foreground">{(p.descrizione || "").slice(0, 44)}</span></button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {(s.tipo || "fisso") === "pratica" && (
              <div className="rounded-md border border-dashed border-input p-2">
                <div className="mb-1 flex items-center justify-between"><span className="text-[11px] font-semibold">Scaglioni prezzo per taglia impianto</span><Button size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => addScag(i)}><Plus className="mr-1 h-3 w-3" /> Scaglione</Button></div>
                {(s.scaglioni || []).map((sc, j) => (
                  <div key={j} className="mb-1 flex items-center gap-2 text-sm">
                    <span className="text-[11px] text-muted-foreground">fino a</span>
                    <Input type="number" value={sc.fino_a_kwp ?? ""} onChange={(e) => setScag(i, j, "fino_a_kwp", e.target.value)} className="h-7 w-24 text-right" data-testid={`servizio-${i}-scag-${j}-kwp`} />
                    <span className="text-[11px] text-muted-foreground">kWp →</span>
                    <Input type="number" value={sc.prezzo ?? ""} onChange={(e) => setScag(i, j, "prezzo", e.target.value)} className="h-7 w-28 text-right" data-testid={`servizio-${i}-scag-${j}-prezzo`} />
                    <span className="text-[11px] text-muted-foreground">€</span>
                    <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => delScag(i, j)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </Card>

      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">Condizioni Generali di Vendita (articoli)</h2>
          <Button size="sm" variant="outline" onClick={addArt} data-testid="terms-add-art"><Plus className="mr-1 h-4 w-4" /> Aggiungi articolo</Button>
        </div>
        {(t.condizioni_generali || []).map((art, i) => (
          <div key={i} className="space-y-1 rounded-md border border-input p-2" data-testid={`terms-art-${i}`}>
            <div className="flex items-center gap-2">
              <Input value={art.titolo} onChange={(e) => setArt(i, "titolo", e.target.value)} className="h-8 flex-1 font-semibold" placeholder="Titolo articolo" data-testid={`terms-art-${i}-titolo`} />
              <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => delArt(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
            <Textarea value={art.testo} onChange={(e) => setArt(i, "testo", e.target.value)} className="min-h-[90px] text-sm" placeholder="Testo articolo" data-testid={`terms-art-${i}-testo`} />
          </div>
        ))}
      </Card>
    </div>
  );
}
